import { Flag, Calendar, Mail, MessageCircle, Megaphone, Play, CheckCircle, AlertCircle, Star, ExternalLink } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'

interface MarcadorEspecial {
  fecha: string
  titulo: string
  descripcion?: string
  url_reporte?: string
  color: string
  tipo: string
  primer_hito?: boolean
}

interface HitosTimelineProps {
  marcadores: MarcadorEspecial[]
}

// Parsear fecha sin problemas de timezone
// Agrega T12:00:00 para evitar que UTC se convierta al día anterior en zonas horarias negativas
const parseDateSafe = (dateString: string): Date => {
  // Si ya tiene hora, usar directamente
  if (dateString.includes('T')) {
    return new Date(dateString)
  }
  // Si es solo fecha (YYYY-MM-DD), agregar mediodía para evitar problemas de timezone
  return new Date(dateString + 'T12:00:00')
}

// Mapeo de tipos a labels para mostrar caracteres especiales correctamente
const TIPO_LABELS: Record<string, string> = {
  evento: 'Evento',
  lanzamiento: 'Lanzamiento',
  campana: 'Campaña',
  milestone: 'Hito',
  otro: 'Otro',
}

const getTipoLabel = (tipo: string): string => {
  return TIPO_LABELS[tipo.toLowerCase()] || tipo
}

// Mapeo de tipos de hito a iconos
const getIconForType = (tipo: string) => {
  const tipoLower = tipo.toLowerCase()

  if (tipoLower.includes('mail') || tipoLower.includes('email') || tipoLower.includes('correo')) {
    return Mail
  }
  if (tipoLower.includes('whatsapp') || tipoLower.includes('mensaje') || tipoLower.includes('sms')) {
    return MessageCircle
  }
  if (tipoLower.includes('campana') || tipoLower.includes('campaña') || tipoLower.includes('campaign')) {
    return Megaphone
  }
  if (tipoLower.includes('inicio') || tipoLower.includes('start') || tipoLower.includes('evento')) {
    return Play
  }
  if (tipoLower.includes('fin') || tipoLower.includes('end') || tipoLower.includes('completado')) {
    return CheckCircle
  }
  if (tipoLower.includes('alerta') || tipoLower.includes('warning') || tipoLower.includes('importante')) {
    return AlertCircle
  }
  if (tipoLower.includes('hito') || tipoLower.includes('milestone') || tipoLower.includes('destacado')) {
    return Star
  }

  return Flag // Icono por defecto
}

export function HitosTimeline({ marcadores }: HitosTimelineProps) {
  const navigate = useNavigate()

  const handleUrlClick = (url: string) => {
    try {
      const urlObj = new URL(url, window.location.origin)
      if (urlObj.origin === window.location.origin) {
        navigate(urlObj.pathname + urlObj.search + urlObj.hash)
      } else {
        window.open(url, '_blank', 'noopener,noreferrer')
      }
    } catch {
      window.open(url, '_blank', 'noopener,noreferrer')
    }
  }

  if (!marcadores || marcadores.length === 0) {
    return null
  }

  // Ordenar marcadores por fecha. El marcador marcado como "primer hito"
  // siempre se muestra como primer elemento, sin importar su fecha.
  const marcadoresOrdenados = [...marcadores].sort((a, b) => {
    if (a.primer_hito && !b.primer_hito) return -1
    if (!a.primer_hito && b.primer_hito) return 1
    return parseDateSafe(a.fecha).getTime() - parseDateSafe(b.fecha).getTime()
  })

  return (
    <Card className="border border-gray-300 dark:border-gray-700">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Flag className="h-4 w-4 text-muted-foreground" />
          Hitos del Proyecto
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 pb-4">
        {/* Container horizontal scrolleable */}
        <div className="overflow-x-auto pb-2">
          <div className="flex items-start gap-0 min-w-max">
            {marcadoresOrdenados.map((marcador, index) => {
              const tituloDisplay = marcador.titulo === 'Inicio de Registros' ? 'Inicio' : marcador.titulo
              const fechaFormateada = parseDateSafe(marcador.fecha).toLocaleDateString('es-EC', {
                day: 'numeric',
                month: 'short',
              })
              const IconComponent = getIconForType(marcador.tipo)
              const isLast = index === marcadoresOrdenados.length - 1

              return (
                <div key={index} className="flex items-start">
                  {/* Hito */}
                  <div className="flex flex-col items-center min-w-[100px]">
                    {/* Icono del hito */}
                    <div
                      className="flex items-center justify-center w-10 h-10 rounded-full border-2 bg-white dark:bg-card shrink-0 shadow-sm"
                      style={{ borderColor: marcador.color }}
                    >
                      <IconComponent
                        className="w-4 h-4"
                        style={{ color: marcador.color }}
                      />
                    </div>

                    {/* Contenido */}
                    <div className="mt-2 text-center px-2">
                      <h4
                        className="font-semibold text-xs leading-tight"
                        style={{ color: marcador.color }}
                      >
                        {tituloDisplay}
                      </h4>
                      <div className="flex items-center justify-center gap-1 text-[10px] text-muted-foreground mt-0.5">
                        <Calendar className="h-2.5 w-2.5" />
                        <span>{fechaFormateada}</span>
                      </div>
                      <Badge className="badge-gradient text-[9px] mt-1">
                        {getTipoLabel(marcador.tipo)}
                      </Badge>
                      {/* Botón de reporte si tiene URL */}
                      {marcador.url_reporte && (
                        <div className="mt-1.5">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-6 px-2 text-[10px] gap-1"
                            style={{
                              borderColor: marcador.color,
                              color: marcador.color,
                            }}
                            onClick={() => handleUrlClick(marcador.url_reporte!)}
                          >
                            <ExternalLink className="h-3 w-3" />
                            Ver Reporte
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Línea conectora */}
                  {!isLast && (
                    <div className="flex items-center h-10 px-1">
                      <div
                        className="w-12 h-0.5"
                        style={{
                          background: `linear-gradient(to right, ${marcador.color}40, ${marcadoresOrdenados[index + 1]?.color || marcador.color}40)`
                        }}
                      />
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
